import { createContext, useContext, useMemo, type ReactNode } from 'react'
import useSWR from 'swr'
import { APIError, fetcher } from './api'
import type { Principal } from '../types'

type AuthContextValue = {
  user?: Principal
  loading: boolean
  error?: Error
  refresh: () => Promise<unknown>
  allowed: (permission: string) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, error, isLoading, mutate } = useSWR<{ user: Principal }>('/v1/auth/me', fetcher, {
    revalidateOnFocus: true,
    shouldRetryOnError: (failure) => !(failure instanceof APIError && failure.status === 401),
  })
  const user = data?.user
  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading: isLoading,
    error,
    refresh: () => mutate(),
    allowed: (permission) => user?.role === 'ADMIN' || user?.permissions[permission] === true,
  }), [user, isLoading, error, mutate])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}

